/*
 * Copyright (c) 2023 Trimble Inc. All rights reserved.
 */

import { Button, Checkbox, CircularProgress, List, ListItemIcon, ListItemText, Paper, Typography, Grid } from '@mui/material';
import { ListItemButton } from '@mui/material';
import { withStyles } from '@mui/styles';
import type { Theme } from '@mui/material/styles';
import * as React from 'react';
import { connect } from 'react-redux';
import { NavLink, useParams, useNavigate } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { getAllModules } from '../common/common';
import { editRoleModuleEndPoint, userRoleRoute } from '../common/constants';
import { loadFromLocalStorage } from '../common/localStorage';
import type { Module } from '../common/UserModel';
import { actionCreators } from '../store/Role';
import Error from './Error';

const styles = (_theme: Theme) => ({
    paper: {
        padding: 20,
        //width:`calc(50vw)`
    },
    data: {
        marginTop: '10px',
        marginBottom: '10px'
    },
    save: {
        marginTop: '50px',
        marginRight: '10px',
        backgroundColor: '#ffbe00'
    },
    cancel: {
        marginTop: '50px',
        border: '1px solid #ffbe00'
    },
    link: {
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'none'
        }
    },
    progressWrap: {
        textAlign: 'center' as 'center',
        marginTop: '20vh'
    },
    checkList: {
        padding: '0px'
    },
    checkListItem: {
        padding: '0px',
        '&:hover': {
            background: 'none'
        },
        '&:active': {
            background: 'none'
        },
        '&:focus': {
            background: 'none'
        }
    },
    checkListItemIcon: {
        marginRight: '0px',
        marginLeft: '0px'
    },
    checkBox: {
        marginRight: '0px',
        paddingLeft: '2px'
    },
    buttonProgress: {
        marginTop: -33,
        marginLeft: 17,
    }
});
interface Props {
    getRole: Function
    selectedRole: any
    isLoading: boolean
    classes?: any
}

const displayFields = [
  { key:'userrole',title:'Role'},
]
function renderData(classes: any, key: any, title: any, role: any) {
    return (
        <div className={classes.data} key={key}>
            <Grid container spacing={8}>
                <Grid>
                    <Typography className={classes.title} color="textSecondary" gutterBottom>
                        {title}
                    </Typography>
                    <Typography>
                        {role[key]}
                    </Typography>
                </Grid>
            </Grid>
        </div>
    );
}

interface ComponentState {
    validationError: string;
    isErrorOpen: boolean;
    selectedModules: { id: number; modulename: string; checked: boolean; }[];
    modules: string[];
    allModules: Module[];
    saveInProgress: boolean;
}

const RoleEdit: React.FC<Props> = (props) => {
    const { classes, getRole, selectedRole, isLoading } = props;
    const { roleid } = useParams<{ roleid: string }>();
    const navigate = useNavigate();

    const [state, setState] = React.useState<ComponentState>({
        validationError: '',
        isErrorOpen: false,
        selectedModules: [],
        modules: [],
        allModules: [],
        saveInProgress: false
    });

    const fetchModules = React.useCallback(async () => {
        let allModules: any = loadFromLocalStorage('All Modules');
        if (allModules == null || allModules === undefined) {
            allModules = await getAllModules();
        }
        setState(s => ({ ...s, allModules }));
    }, []);

    React.useEffect(() => {
        fetchModules();
    }, [fetchModules]);

    React.useEffect(() => {
        if (selectedRole === undefined || selectedRole == null) {
            if (roleid && parseInt(roleid)){
                getRole(roleid);
            }
            else{
                navigate(userRoleRoute);
            }
            return;
        }
        if (selectedRole === "norole") {
            navigate(userRoleRoute);
            return;
        }
        
        const roleIdstoValidate: Array<number> = [6, 7];
        const moduleIdstoHide: Array<number> = [6, 10, 11];
        const validateModule: boolean = (roleIdstoValidate.find(id => id === selectedRole.id) != null);

        const modules: Array<string> = selectedRole['modules'].split(',');
        const selectedModules: Array<{ id: number; modulename: string; checked: boolean; }> = [];
        state.allModules.forEach((allModule: Module) => {
            const m: any = selectedRole['selectedModules'].find((module: Module) => {
                return module.id === allModule.id;
            });
            if (m != null) {
                selectedModules.push({ id: allModule.id, modulename: allModule.modulename, checked: true });
            }
            else {
                if (!validateModule || (validateModule && moduleIdstoHide.find(id => {
                    return id === allModule.id;
                }) == null)) {
                    selectedModules.push({ id: allModule.id, modulename: allModule.modulename, checked: false });
                }
            }
        });

        setState(s => ({ ...s, modules, selectedModules }));

    }, [selectedRole, state.allModules, getRole, roleid, navigate]);


    const handleSave = () => {
        const checkedmodules: any = [];
        state.selectedModules.forEach((selectedmodule: any) => {
            if (selectedmodule.checked === true) {
                checkedmodules.push(selectedmodule['id']);
            }
        });
        sendEditRoleRequest(checkedmodules);
    };

    const sendEditRoleRequest = (modules: any): any => {
      const token: string = loadFromLocalStorage("AuthToken");
      const encodedToken = 'Bearer ' + token;
        const headerDict: any = {
            'Authorization': encodedToken,
            'Content-Type': 'application/json'
        };
        const requestBody = {
            roleid: selectedRole['id'],
            modules: `${modules.join(';')}`
        };
        setState(s => ({ ...s, validationError: '', isErrorOpen: false, saveInProgress: true }));
        try {
            if (navigator.onLine) {
                fetch(editRoleModuleEndPoint, {
                    headers: new Headers(headerDict),
                    body: JSON.stringify(requestBody),
                    method: 'POST'
                })
                    .then(async (response: any) => {
                        const reader = response.body.getReader();
                        let responsetext = '';
                        await reader.read().then((x: any) => {
                            responsetext = new TextDecoder('utf-8').decode(x.value);
                        });
                        switch (response.status) {
                            case 200:
                                setState(s => ({ ...s, validationError: '', isErrorOpen: false, saveInProgress: false }));
                                navigate(userRoleRoute);
                                break;
                            case 400:
                                setState(s => ({ ...s, validationError: responsetext, isErrorOpen: true, saveInProgress: false }));
                                break;
                            default:
                                setState(s => ({ ...s, validationError: '', isErrorOpen: true, saveInProgress: false }));
                        }
                    });
            }
        }
        catch (e) { }
    }
    const handleToggle = (index: any) => {
        setState(
            (prevState: any) => {
                const selectedModules = Object.assign([], prevState.selectedModules);
                selectedModules[index].checked = selectedModules[index].checked !== true;
                return {
                    ...prevState,
                    selectedModules
                };
            });
    };
    const checkList = (items: any, key: any, value: any) => {
        return (
            <List dense role="list" className={classes.checkList}>
                {items.map((item: any, index: any) => {
                    return (
                        <ListItemButton className={classes.checkListItem} key={item[key]} role="listitem" onClick={() => { handleToggle(index); }}>
                            <ListItemIcon className={classes.checkListItemIcon}>
                                <Checkbox
                                    checked={item['checked']}
                                    tabIndex={-1}
                                    disableRipple
                                    className={classes.checkBox}
                                />
                            </ListItemIcon>
                            <ListItemText id={item[key]} primary={item[value]} />
                        </ListItemButton>
                    );
                })}
                <ListItemButton />
            </List>
        );
    };

    return (
        isLoading ?
            <div className={classes.progressWrap}>
                <CircularProgress color="secondary" />
            </div> :
            <div>
                {selectedRole != null && <Paper className={classes.paper}>
                    <form>
                        {displayFields.map((field: any) => { return renderData(classes, field.key, field.title, selectedRole); })}
                        <Grid container>
                            <Grid>
                                <Typography className={classes.title} color="textSecondary" gutterBottom>
                                    {`Modules`}
                                </Typography>
                                {checkList(state.selectedModules, 'id', 'modulename')}
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid>
                                <Button disabled={state.saveInProgress || !navigator.onLine} className={classes.save} onClick={handleSave}>Save</Button>
                                {state.saveInProgress && <div className={classes.buttonProgress}><CircularProgress size={24} /></div>}
                            </Grid>
                            <Grid>
                                <NavLink className={classes.link} to={userRoleRoute}><Button disabled={state.saveInProgress}
                                    className={classes.cancel}>Cancel</Button></NavLink>
                            </Grid>
                        </Grid>
                        <Error errorMessage={state.validationError} isopen={state.isErrorOpen} />
                    </form>
                </Paper>}
            </div>
    );
}



const mapStateToProps = (state: any) => ({
  selectedRole: state.roleModule.selectedRole,
  isLoading:state.roleModule.isLoading
});
const mapDispatchtoProps = (dispatch: any) => bindActionCreators(actionCreators, dispatch);

export default connect(mapStateToProps, mapDispatchtoProps)(withStyles(styles)(RoleEdit));